/* ══════════════════════════════════════════════════════════════════════════
 * SIGN IN — THE FIRST SCREEN IN ELEAT THAT ACTUALLY DOES SOMETHING
 *
 * ⚠️ EVERY WORD ON THIS SCREEN IS COPIED, NOT WRITTEN. Source:
 * `Eleater Prototype/04_Email_SignUp.html`, the `.returning` block — labels,
 * placeholders, the "Stay signed in" line, the sentence under it, the button
 * and the way out. RULE 4: DO NOT INVENT ELEAT. Not copy, not headings, not
 * buttons. If a word here is wrong it is wrong on that screen too, and both
 * change together.
 *
 * ⚠️ AND IT IS IDENTIFIER AND PASSWORD, NOTHING ELSE. Al, 16 September 2026:
 * "Type in your phone number and your password. That's it." No Google button —
 * removed on his instruction and not coming back until he approves it.
 * ══════════════════════════════════════════════════════════════════════════ */

import { useEffect, useState } from 'react';
import { signIn, saveToken, getToken, announceSignedIn } from './api.js';
import { GoogleButton } from './google.jsx';
import { go } from './nav.js';
import { returnParam, withReturn } from './gate.jsx';
import { restoreHome } from './place.js';
import { Eye } from './Eye.jsx';
import { PhoneRow, joinPhone, deviceDial, useChannels, WA_PATH } from './phone.jsx';

/* ══════════════════════════════════════════════════════════════════════════
 * ⛔⛔ SIGNING IN GOES STRAIGHT TO HOME. THERE IS NO SCREEN IN BETWEEN.
 *
 * Al, 29 September 2026, on a screenshot of what used to be here — a panel
 * reading "Signed in as Al-Noor." · "Eleater no. EL-5535-3141" · "Find
 * somewhere to eat" · "Sign out":
 *
 *   "Since when do we ever have that? This is a screen that you've snuck in
 *    somewhere. It's not even in my locked screens… I've never agreed to this."
 *
 * ⚠️ HE IS RIGHT, AND MY OWN COMMENT ON IT SAID SO IN CAPITALS: *"THIS IS A
 * PROOF PANEL, NOT A PRODUCT SCREEN. It exists so the whole path can be seen
 * working — browser to server to database and back — before a second screen is
 * built on top of it. HOME REPLACES IT."*  Home was built on 23 September and
 * nothing came back to delete the scaffold, so every sign-in since has landed
 * on it. **Scaffolding with a note saying it is temporary is still scaffolding
 * on a founder's screen.**
 *
 * ⚠️ AND NOTHING REPLACES IT, BECAUSE NOTHING SHOULD. `07_Welcome.html` — "You
 * are now an Eleater" — belongs to JOINING and happens once, at the end of
 * `Join.jsx`. Signing back in is not joining. Every sign-in screen in Eleat
 * ends at Home.
 * ══════════════════════════════════════════════════════════════════════════ */

export default function SignIn() {
  const [identifier, setIdentifier] = useState('');
  /* ⚠️ THE WHATSAPP DOOR — `?signin=1&by=whatsapp` (8 Oct 2026): the number and
     the password, `whatsapp_onboarding_new.html`'s sign-in mode. Drawn only
     when WhatsApp codes are switched on (GET /auth/channels). */
  const { whatsapp: waOn } = useChannels();
  /* WHATSAPP FIRST ONCE IT IS ON — Al-Noor, 9 Oct 2026: "WhatsApp comes in
     first everywhere, even in North America". With WhatsApp switched on, Sign
     in opens on the WhatsApp door; the email door is one tap below it. */
  const byWa = waOn && new URLSearchParams(window.location.search).get('by') !== 'email';
  const [dial, setDial] = useState(deviceDial);
  const [num, setNum] = useState('');
  const who = byWa ? joinPhone(dial, num) : identifier;
  const [password, setPassword]     = useState('');
  const [stay, setStay]             = useState(true);   // ticked, as on the locked screen
  /* ⚠️ THE PASSWORD IS HIDDEN UNTIL ASKED FOR, AND GOES BACK TO HIDDEN ON ITS
   * OWN. It is not remembered between visits and is never the starting state —
   * a field that opens in the clear is a password shown to whoever is standing
   * behind them, for a reason nobody asked for. */
  const [show, setShow]             = useState(false);
  const [busy, setBusy]             = useState(false);
  const [error, setError]           = useState(null);

  /* ⚠️ ALREADY SIGNED IN AND THIS ADDRESS WAS OPENED ANYWAY — go to Home
   * rather than draw a sign-in form at somebody who is signed in. That is the
   * back button, a bookmark, or a reload after signing in. */
  useEffect(() => {
    if (getToken()) go(returnParam(), { replace: true });
  }, []);

  const ready = who.trim() !== '' && password !== '' && !busy;

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    /* never greyed out (Al-Noor, 10 Oct 2026): pressed too early it says what is missing */
    if (!ready) { setError({ text: 'Enter your email address and your password.' }); return; }
    setBusy(true);
    setError(null);
    try {
      const { token: t } = await signIn({ identifier: who, password });
      saveToken(t, stay);
      setPassword('');                       // never leave it sitting in the field
      /* ⚠️ HOME, AND `replace` RATHER THAN `href` — so the back button goes to
         wherever they came from and never to the sign-in form they have just
         used. ⚠️ The `finally` below still runs on this return, which is what
         JavaScript does and is correct here: the button goes idle for the
         moment before the page turns, and a second tap re-signs-in the same
         person rather than doing any harm. */
      /* ⚠️ IN PLACE, NOT A RELOAD — Al-Noor, 9 Oct 2026: signing in "took a
         while… to put me onto the home page". The page and the code are
         already here; only Home's own answers need to travel. */
      await restoreHome();
      announceSignedIn();
      go(returnParam(), { replace: true });
      return;
    } catch (err) {
      /* ⚠️ THE SERVER'S OWN SENTENCE, UNCHANGED. It says the same thing for a
       * wrong password and an account that does not exist, on purpose, so a
       * stranger cannot learn who is on Eleat by typing addresses at it.
       * `offline` is a different thing entirely and is marked as such — an
       * Eleater told their password is wrong will change a password that was
       * fine. */
      setError({ text: err.message, offline: !!err.offline });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="screen signinf" onSubmit={submit} noValidate>
      {/* ⚠️ THE WAY BACK — 30 September 2026. `04_Email_SignUp.html` is this
          form in its sign-in mode and carries the same `.top` · `.back` (#b1)
          as joining, which returns to the Welcome door with `return=` intact.
          Without it a visitor who arrived from the "Become an Eleater" sheet
          had no way back but the browser's own button. */}
      <div className="top">
        <a className="back" href={withReturn('?welcome=1', returnParam())} aria-label="Back">‹</a>
      </div>
      {/* ⚠️ THE REAL MARK. `eleat_mark.png` over `eleat_word.png` at the locked
          sizes — the word "Eleat" typed out in a font was mine, not Eleat's. */}
      <div className="logo">
        <img className="lmark" src="/eleat_mark.png" alt="" />
        <img className="lword" src="/eleat_word.png" alt="Eleat" />
      </div>

      {/* ══════════════════════════════════════════════════════════════════
        * ⚠️ "Welcome back" — AL ASKED WHETHER THIS EXISTED AND IT DOES. IT
        * BELONGS HERE, BEFORE THE PASSWORD, NOT AFTER IT.
        *
        * He said, 29 September 2026: *"Didn't we also have something that says
        * welcome back?… Or just I'm not I think I'm wrong. Just correct me or
        * tell me."*  **He was right and this screen had neither line.** It
        * opened on the logo and went straight to "Email address".
        *
        * Both locked doors carry it, and it is HIS OWN WORDING, 16 September:
        * *"already you have an account. Welcome back. Type in your phone or
        * email…"*
        *   · `02_Welcome.html` — `h1 = isSignin ? 'Welcome back' : 'Welcome to
        *     Eleat'`
        *   · `04_Email_SignUp.html` — `h.textContent = 'Welcome back'`
        *
        * ⚠️ THE SUB-LINE IS THE EMAIL DOOR'S OWN, NOT THE COMBINED SCREEN'S.
        * `02_Welcome.html` says *"Your number or your email, and your
        * password"* because BOTH doors live on it. **This screen is the email
        * door**, and `04_Email_SignUp.html`'s `[data-signin]` span says
        * **"Enter your email address and your password."**  Taking the other
        * one would offer a number on a screen with no number field.
        *
        * ⚠️ AND IT IS NOT A GREETING BY NAME. Eleat does not know who you are
        * until you have signed in, so "Welcome back, Al-Noor" here would mean
        * asking the server who owns an address before anybody proves they do —
        * which tells a stranger whether an address is on Eleat. `SignIn.jsx`'s
        * own error already refuses to leak that, deliberately.
        * ══════════════════════════════════════════════════════════════════ */}
      <div className="copy">
        <h1>Welcome back</h1>
        {byWa
          ? <p className="sub">Enter your number and your password.</p>
          : <p className="sub">Enter your email address and your password.</p>}
      </div>

      <main>
        {/* ⚠️ "Email address" IS THE LOCKED SCREEN'S OWN LABEL, and I had
            written "Email or mobile number" here — an invention, and the exact
            fault this project has been clearing all week.
            04_Email_SignUp.html: "THE NUMBER IS A JOINING FIELD — signing in by
            email asks for the address and the password", quoting Al: "It should
            just ask for a fucking number and or email."
            ⚠️ AND THE QUESTION I RAISED UNDER IT WAS NOT A QUESTION. I asked Al
            whether this label should say "Email or mobile number", because the
            SERVER takes either — and he answered: "no we have 2 different sign
            ins. one by whatsapp and one by email. why are you confusing this."
            He is right, and 02_Welcome.html has said so all along: two rows,
            "WhatsApp — Your number and your password" and "Email — Your address
            and your password". THIS SCREEN IS THE EMAIL DOOR. It asks for an
            address because that is the door it is. The WhatsApp door is its own
            screen, asks for the number, and is still to be built.
            ⚠️ THE SERVER TAKING EITHER IS NOT AMBIGUITY, IT IS TWO DOORS INTO
            ONE ROOM. I turned an implementation detail into a product question
            and handed it to Al as a decision. Read the screen the product is
            made of before inventing a choice for him to make. */}
        {byWa ? (<>
          <div className="label">Mobile number</div>
          <PhoneRow dial={dial} number={num} onDial={setDial} onNumber={setNum} />
        </>) : (<>
          <div className="label">Email address</div>
          <input
            className="tin"
            type="text"
            inputMode="email"
            autoComplete="username"
            autoCapitalize="off"
            spellCheck={false}
            placeholder="you@example.com"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
          />
        </>)}

        <div className="label" style={{ marginTop: 16 }}>Password</div>
        {/* ⚠️ THE EYE. Al, 17 September 2026: "there should be a eye that shows
            the pw as well. you forgot that." The SVG, the classes and the aria
            below are lifted from 04_Email_SignUp.html's `#suEye` exactly.
            ⚠️ AND THE LOCKED SCREEN HAS THE SAME GAP. It puts an eye on joining,
            confirm, reset and reset-confirm — and none on signing back in. The
            one field you type most often, on a phone, one-handed, is the one
            with no way to check it. Added there too, in the same change. */}
        <div className="pwwrap">
          <input
            className="tin"
            type={show ? 'text' : 'password'}
            /* ⚠️ current-password, never new-password. It is what tells a password
               manager to FILL IN the one it has rather than offer a fresh one. */
            autoComplete="current-password"
            placeholder="Your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {/* ⚠️ THE EYE MOVED TO `Eye.jsx` ON 21 SEPTEMBER 2026, and the forty
              lines of SVG that were written out here are gone rather than
              copied. Joining has two password fields and the reset screen has
              two; six copies of this icon is six places for the crossed-out
              eye to stop matching the open one, on screens nobody compares.
              Everything Al reported about it — the state, the icon, the label
              — is preserved in that file, with the reasons. */}
          <Eye on={show} onToggle={() => setShow((v) => !v)} />
        </div>

        <label className="stay">
          <input
            type="checkbox"
            checked={stay}
            onChange={(e) => setStay(e.target.checked)}
          />
          <span>Stay signed in</span>
        </label>
        {/* ⚠️ IT SAYS WHAT IT DOES AND NOT A WORD MORE — verbatim from the
            locked screen. There is no session table, so this is true of this
            device and is not a promise about any other. */}
        <div className="staywhy">Eleat will not ask again on this device.</div>

        <div className="err" data-offline={error?.offline ? 'true' : 'false'} aria-live="polite">
          {error?.text || ''}
        </div>
      </main>

      <footer>
        <button className="cta" type="submit">
          Sign in
        </button>
        {/* ⚠️ BOTH WAYS OUT ARE NOW REAL — 21 September 2026. Until today each
            of these printed "Not built yet", which meant nobody could join
            Eleat and nobody could recover a password. Everything built in
            React before today was for people who already had an account, and
            the only way to get one was a script run by hand.

            They are links rather than buttons because they go somewhere, and a
            link can be opened in a new tab, shared, and reached by pressing
            Enter — which a `<button>` inside a `<form>` cannot do without
            submitting the form it sits in. */}
        {/* "Join Eleat" opens the Welcome door, where every way of joining is
            (email, Google, WhatsApp when it is on) — not straight into the email
            form (Al-Noor, 30 Oct 2026). */}
        {/* Sign in with Google straight under Sign in, the same shape — Al-Noor,
            30 Oct 2026; then Forgot your password? */}
        <GoogleButton mode="signin" />
        <a className="forgot" href={byWa ? '?reset=1&by=whatsapp' : '?reset=1'}>Forgot your password?</a>
        {/* the other door — `02_Welcome.html` .signlist's own two rows, drawn
            only while WhatsApp is switched on */}
        {waOn && (byWa ? (
          <a className="signrow" href={withReturn('?signin=1&by=email', returnParam())}>
            <span className="ic"><svg width="21" height="21" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
              <rect x="2.5" y="4.5" width="19" height="15" rx="2.5" /><path d="m3.5 6.5 8.5 6.5 8.5-6.5" />
            </svg></span>
            <span>Email<span className="sub">Your address and your password</span></span>
            <span className="go">›</span>
          </a>
        ) : (
          <a className="signrow" href={withReturn('?signin=1&by=whatsapp', returnParam())}>
            <span className="ic"><svg width="22" height="22" viewBox="0 0 24 24" className="wa"
              fill="currentColor" aria-hidden="true"><path d={WA_PATH} /></svg></span>
            <span>WhatsApp<span className="sub">Your number and your password</span></span>
            <span className="go">›</span>
          </a>
        ))}
        <a className="forgot" href={withReturn('?welcome=1', returnParam())}>New to Eleat? <span className="joinlink">Join Eleat</span></a>
      </footer>
    </form>
  );
}
